/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useCallback, useMemo } from "react"
import { FacetTypes, GrabXDataTypes, useGrabXFeatureFlag } from "grabx"
import { MerchantPortalFeatureFlag } from "grabx/constants"
import { generateDefaultFacets, withObserverModeFacet } from "grabx/facets"
import { StoreMigrationMissingInfoSummary } from "types/api"
import { OutletStore } from "types/models/menu"

interface BuildMenuStoreMigrationMissingInfoAdapterResultArgs {
  stores: OutletStore[]
  isFeatureEnabled: boolean
  isMultiOutletMerchant: boolean
  isLoading: boolean
  isError: boolean
  summary?: StoreMigrationMissingInfoSummary
}

interface BuildMenuStoreMigrationMissingInfoAdapterResult {
  isEnabled: boolean
  isLoading: boolean
  isError: boolean
  isEligibleMerchant: boolean
  hasAnyStoreMissingInfo: boolean
  shouldShowBanner: boolean
  shouldShowBlankMenuCta: boolean
  missingInfoStoreIDs: string[]
}

export interface UseMenuStoreMigrationMissingInfoAdapterArgs {
  stores: OutletStore[]
  isMultiOutletMerchant: boolean
  merchantGroupID?: string
  isStoreListLoading: boolean
  isStoreListError: boolean
  summary?: StoreMigrationMissingInfoSummary
}

export interface UseMenuStoreMigrationMissingInfoAdapterResult
  extends BuildMenuStoreMigrationMissingInfoAdapterResult {
  isStoreMissingInfo: (store: OutletStore) => boolean
}

export const MENU_MIGRATION_PREVIEW_MOCK_QUERY_KEY = "menuMigrationPreviewMock"
export const MENU_MIGRATION_PREVIEW_MOCK_STORE_ID = "preview-mock-store-id"

const EMPTY_RESULT: BuildMenuStoreMigrationMissingInfoAdapterResult = {
  isEnabled: false,
  isLoading: false,
  isError: false,
  isEligibleMerchant: false,
  hasAnyStoreMissingInfo: false,
  shouldShowBanner: false,
  shouldShowBlankMenuCta: false,
  missingInfoStoreIDs: [],
}

const getStoreLevelMissingInfo = (store: OutletStore) => {
  return {
    hasMissingInfo: store?.migrationMissingInfo?.hasMissingInfo,
    isEligibleMerchant: store?.migrationMissingInfo?.isEligibleMerchant,
    isMenuCompletelyBlank: store?.migrationMissingInfo?.isMenuCompletelyBlank,
  }
}

const getStoreIDsWithMissingInfo = (stores: OutletStore[]): string[] => {
  return stores
    .filter(store => getStoreLevelMissingInfo(store).hasMissingInfo)
    .map(store => store.merchantID)
}

const getBlankMenuStoreIDs = (stores: OutletStore[]): string[] => {
  return stores
    .filter(store => !!getStoreLevelMissingInfo(store).isMenuCompletelyBlank)
    .map(store => store.merchantID)
}

export const isMenuMigrationPreviewMockEnabled = (search?: string): boolean => {
  const isNonProductionEnvironment = process.env.NODE_ENV !== "production"

  if (!isNonProductionEnvironment) return false
  if (typeof window === "undefined" && !search) return false

  const locationSearch = search ?? window.location.search

  const searchParams = new URLSearchParams(locationSearch)
  return searchParams.get(MENU_MIGRATION_PREVIEW_MOCK_QUERY_KEY) === "1"
}

export const useMenuMigrationMissingInfoFeatureFlag = (
  merchantGroupID?: string,
) => {
  const facets = withObserverModeFacet({
    ...generateDefaultFacets(),
    ...(merchantGroupID && {
      [FacetTypes.MexMerchant]: merchantGroupID,
    }),
  })

  return useGrabXFeatureFlag<boolean>(
    MerchantPortalFeatureFlag.portalEnableMenuStoreMigrationMissingInfoWarning,
    GrabXDataTypes.Boolean,
    false,
    facets,
  )
}

export const buildMenuStoreMigrationMissingInfoAdapterResult = ({
  stores,
  isFeatureEnabled,
  isMultiOutletMerchant,
  isLoading,
  isError,
  summary,
}: BuildMenuStoreMigrationMissingInfoAdapterResultArgs): BuildMenuStoreMigrationMissingInfoAdapterResult => {
  if (!isFeatureEnabled)
    return {
      ...EMPTY_RESULT,
      isLoading,
      isError,
    }

  if (isLoading || isError || !isMultiOutletMerchant || stores.length <= 0) {
    return {
      ...EMPTY_RESULT,
      isEnabled: true,
      isLoading,
      isError,
    }
  }

  const missingInfoStoreIDs = summary?.missingInfoStoreIDs?.length
    ? summary.missingInfoStoreIDs
    : getStoreIDsWithMissingInfo(stores)

  const blankMenuStoreIDs = getBlankMenuStoreIDs(stores)

  const hasAnyStoreMissingInfo =
    summary?.hasAnyStoreMissingInfo ?? missingInfoStoreIDs.length > 0

  const hasAnyBlankMenuStore =
    summary?.isMenuCompletelyBlank ?? blankMenuStoreIDs.length > 0

  const isEligibleFromStoreLevel = stores.some(
    store => !!getStoreLevelMissingInfo(store).isEligibleMerchant,
  )

  const isEligibleMerchant =
    summary?.isEligibleMerchant ?? isEligibleFromStoreLevel

  const shouldShowBanner = isEligibleMerchant && hasAnyStoreMissingInfo
  const shouldShowBlankMenuCta = shouldShowBanner && hasAnyBlankMenuStore

  return {
    isEnabled: true,
    isLoading: false,
    isError: false,
    isEligibleMerchant,
    hasAnyStoreMissingInfo,
    shouldShowBanner,
    shouldShowBlankMenuCta,
    missingInfoStoreIDs: shouldShowBanner ? missingInfoStoreIDs : [],
  }
}

const useMenuStoreMigrationMissingInfoAdapter = ({
  stores,
  isMultiOutletMerchant,
  merchantGroupID,
  isStoreListLoading,
  isStoreListError,
  summary,
}: UseMenuStoreMigrationMissingInfoAdapterArgs): UseMenuStoreMigrationMissingInfoAdapterResult => {
  const isPreviewMockEnabled = isMenuMigrationPreviewMockEnabled()

  const {
    data: isRemoteFlagEnabled = false,
    isLoading: isRemoteFlagLoading,
    isError: isRemoteFlagError,
  } = useMenuMigrationMissingInfoFeatureFlag(merchantGroupID)

  const isFeatureEnabled = isRemoteFlagEnabled

  const result = useMemo(() => {
    if (isPreviewMockEnabled) {
      const previewStoreIDs = [
        MENU_MIGRATION_PREVIEW_MOCK_STORE_ID,
        stores[0]?.merchantID,
      ].filter(Boolean) as string[]

      return {
        isEnabled: true,
        isLoading: false,
        isError: false,
        isEligibleMerchant: true,
        hasAnyStoreMissingInfo: true,
        shouldShowBanner: true,
        shouldShowBlankMenuCta: true,
        missingInfoStoreIDs: previewStoreIDs,
      }
    }

    return buildMenuStoreMigrationMissingInfoAdapterResult({
      stores,
      isFeatureEnabled,
      isMultiOutletMerchant,
      isLoading: isStoreListLoading || isRemoteFlagLoading,
      isError: isStoreListError || !!isRemoteFlagError,
      summary,
    })
  }, [
    stores,
    isPreviewMockEnabled,
    isFeatureEnabled,
    isMultiOutletMerchant,
    isStoreListLoading,
    isStoreListError,
    summary,
    isRemoteFlagLoading,
    isRemoteFlagError,
  ])

  const missingInfoStoreIDsSet = useMemo(
    () => new Set(result.missingInfoStoreIDs),
    [result.missingInfoStoreIDs],
  )

  const isStoreMissingInfo = useCallback(
    (store: OutletStore) => missingInfoStoreIDsSet.has(store.merchantID),
    [missingInfoStoreIDsSet],
  )

  return {
    ...result,
    isStoreMissingInfo,
  }
}

export default useMenuStoreMigrationMissingInfoAdapter
